import { useMemo, useState } from 'react';
import { ShieldCheck, Timer } from 'lucide-react';
import { Button } from '@travellegacy/nexops-design-system';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { ActiveDemandsCard } from '../components/ActiveDemandsCard';
import { DistributionCard } from '../components/DistributionCard';
import { MetricsByTypeCard } from '../components/MetricsByTypeCard';
import { OverviewKPIs } from '../components/OverviewKPIs';
import { PeriodFilter, buildPresetRange, type PeriodRange } from '../components/PeriodFilter';
import { ResponsibleMetricsCard } from '../components/ResponsibleMetricsCard';
import { SLAConfigModal } from '../components/SLAConfigModal';
import { ThroughputChart } from '../components/ThroughputChart';
import { WorkloadCard } from '../components/WorkloadCard';
import { DemandsTable } from '../components/DemandsTable';
import {
  useActiveDemands,
  useDemands,
  useDemandsOverview,
  useResponsibleMetrics,
  useThroughput,
  useTypeMetrics,
  useWorkload,
} from '../hooks/useDemands';

/**
 * Página de métricas de demandas do Kanban do Notion.
 *
 * Rota: `/demands` (protegida por `CLIENT_OPERATIONS`).
 */
export function DemandsPage() {
  const [search, setSearch] = useState('');
  const searchDebounced = useDebounce(search, 400);
  const [page, setPage] = useState(1);
  const [slaModalOpen, setSlaModalOpen] = useState(false);

  // Filtros avançados da tabela.
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterResponsible, setFilterResponsible] = useState<string>('');
  const [filterActive, setFilterActive] = useState<'' | 'true' | 'false'>('');
  const [filterHasRework, setFilterHasRework] = useState<boolean>(false);

  // Filtro de período compartilhado entre overview, by-responsible e by-type.
  // Default: últimos 7 dias, alinhado ao default do backend.
  const [period, setPeriod] = useState<PeriodRange>(() => buildPresetRange('7d'));

  const listFilters = useMemo(
    () => ({
      page,
      limit: 50,
      search: searchDebounced || undefined,
      type: filterType || undefined,
      status: filterStatus || undefined,
      responsibleName: filterResponsible || undefined,
      isActive:
        filterActive === '' ? undefined : filterActive === 'true' ? true : false,
      hasRework: filterHasRework || undefined,
    }),
    [
      page,
      searchDebounced,
      filterType,
      filterStatus,
      filterResponsible,
      filterActive,
      filterHasRework,
    ]
  );

  function clearFilters() {
    setFilterType('');
    setFilterStatus('');
    setFilterResponsible('');
    setFilterActive('');
    setFilterHasRework(false);
    setPage(1);
  }

  const periodFilters = useMemo(
    () => ({ from: period.from, to: period.to }),
    [period.from, period.to]
  );

  const activeQuery = useActiveDemands();
  const overviewQuery = useDemandsOverview(periodFilters);
  const responsibleQuery = useResponsibleMetrics(periodFilters);
  const typeQuery = useTypeMetrics(periodFilters);
  const workloadQuery = useWorkload();
  const throughputQuery = useThroughput(periodFilters);
  const listQuery = useDemands(listFilters);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Timer className="h-7 w-7 text-primary" />
            Demandas
          </h1>
          <p className="text-muted-foreground mt-1">
            Tempo de execução das demandas do Kanban do Notion. Cada ciclo em "Em andamento" é
            registrado e somado ao tempo total da demanda.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <PeriodFilter value={period} onChange={setPeriod} />
          <Button variant="outline" onClick={() => setSlaModalOpen(true)}>
            <ShieldCheck className="h-4 w-4 mr-2" />
            Configurar SLAs
          </Button>
        </div>
      </div>

      {/* Cockpit de KPIs — visão executiva do estado das demandas. */}
      <OverviewKPIs
        overview={overviewQuery.data?.data}
        isLoading={overviewQuery.isLoading}
      />

      <ActiveDemandsCard
        demands={activeQuery.data?.data ?? []}
        isLoading={activeQuery.isLoading}
        stuckThresholdMs={overviewQuery.data?.data.stuckThresholdMs}
      />

      {/* Carga atual + throughput (visão diária) */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <WorkloadCard
          workload={workloadQuery.data?.data ?? []}
          stuckThresholdMs={workloadQuery.data?.stuckThresholdMs}
          isLoading={workloadQuery.isLoading}
        />
        <ThroughputChart
          data={throughputQuery.data?.data ?? []}
          isLoading={throughputQuery.isLoading}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ResponsibleMetricsCard
          metrics={responsibleQuery.data?.data ?? []}
          isLoading={responsibleQuery.isLoading}
        />
        <MetricsByTypeCard
          metrics={typeQuery.data?.data ?? []}
          isLoading={typeQuery.isLoading}
        />
      </div>

      <DistributionCard periodFilters={periodFilters} />

      <DemandsTable
        demands={listQuery.data?.data ?? []}
        total={listQuery.data?.pagination.total ?? 0}
        page={listQuery.data?.pagination.page ?? page}
        totalPages={listQuery.data?.pagination.totalPages ?? 1}
        isLoading={listQuery.isLoading}
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        onPageChange={setPage}
        filterType={filterType}
        filterStatus={filterStatus}
        filterResponsible={filterResponsible}
        filterActive={filterActive}
        filterHasRework={filterHasRework}
        onFilterTypeChange={(v) => {
          setFilterType(v);
          setPage(1);
        }}
        onFilterStatusChange={(v) => {
          setFilterStatus(v);
          setPage(1);
        }}
        onFilterResponsibleChange={(v) => {
          setFilterResponsible(v);
          setPage(1);
        }}
        onFilterActiveChange={(v) => {
          setFilterActive(v);
          setPage(1);
        }}
        onFilterHasReworkChange={(v) => {
          setFilterHasRework(v);
          setPage(1);
        }}
        onClearFilters={clearFilters}
      />

      <SLAConfigModal open={slaModalOpen} onOpenChange={setSlaModalOpen} />
    </div>
  );
}

export default DemandsPage;
